import { useState } from 'react';
import { JEWELRY_TYPES, FINISHING_TYPES, FINDINGS_BY_TYPE, calcTotalStoneWeight } from '@/utils/cad-sheet';
import {
  STONE_SHAPES,
  STONE_TYPES,
  STONE_OWNERSHIPS,
  STONE_POSITION_CATEGORIES,
  METAL_TYPES,
  METAL_COLOURS,
  METAL_KARATS,
} from '@/utils/constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ClientSelect } from '@/lib/ClientSelect';
import { Plus, Trash2, RotateCcw, Grid, Upload, X } from 'lucide-react';
import { StoneImportDialog } from './StoneImportDialog';

// Infer a metal_type for legacy CAD sheets that were saved before metal_type
// was introduced — keeps the per-type karat/colour dropdowns sensible.
function inferMetalType(karat, color) {
  if (!karat) return 'gold';
  const k = String(karat).toUpperCase();
  if (k.endsWith('K') || ['24K','22K','18K','14K','10K','9K'].includes(k)) return 'gold';
  if (['S925', '999'].includes(k)) return 'silver';
  if (k === '950') return (color || '').toLowerCase() === 'pt' || (color || '').toLowerCase().startsWith('pt') ? 'platinum' : 'silver';
  if (k.startsWith('PT') || k === 'PLATINUM') return 'platinum';
  if (k === 'BRASS' || k === 'ELECTRUM') return 'brass';
  return 'gold';
}

export function InputForm({
  data, onUpdateField,
  onAddStone, onImportStones, onUpdateStone, onRemoveStone,
  onAddMetal, onUpdateMetal, onRemoveMetal,
  onAddFinding, onUpdateFinding, onRemoveFinding,
  onSetGrossWeightManual,
  onSetEngravingImage, onRemoveEngravingImage,
  onAddCustomerImage, onRemoveCustomerImage,
  onAddStampingImage, onRemoveStampingImage,
  onReset,
}) {
  const [importOpen, setImportOpen] = useState(false);
  const [customFinishing, setCustomFinishing] = useState('');
  const findingsOptions = FINDINGS_BY_TYPE[data.jewelryType] || [];

  const totalStoneWeight = calcTotalStoneWeight(data.stones);
  const totalStoneQty = data.stones.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
  const totalStoneWtPerPc = data.stones.reduce((sum, s) => sum + ((parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0)), 0);

  const handleCustomerImageSelect = (e) => {
    const files = e.target.files;
    if (files) Array.from(files).forEach(file => onAddCustomerImage(file));
    e.target.value = '';
  };

  const handleEngravingImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) onSetEngravingImage(file);
  };

  return (
    <div className="space-y-5">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label htmlFor="styleNo" className="text-xs">Style No.</Label>
          <Input id="styleNo" value={data.styleNo} onChange={e => onUpdateField('styleNo', e.target.value)} placeholder="e.g. R2-216680" className="h-8 text-sm" data-testid="cad-style-no" />
        </div>
        <div>
          <Label htmlFor="customerName" className="text-xs">Customer Name <span className="text-muted-foreground">(internal)</span></Label>
          <ClientSelect
            value={data.customerName}
            onChange={v => onUpdateField('customerName', v)}
            placeholder="Select client..."
            className="w-full h-8 text-xs"
          />
        </div>
        <div>
          <Label htmlFor="customerDesignNo" className="text-xs">Customer Design No. <span className="text-muted-foreground">(internal)</span></Label>
          <Input id="customerDesignNo" value={data.customerDesignNo} onChange={e => onUpdateField('customerDesignNo', e.target.value)} placeholder="e.g. CD-001" className="h-8 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Jewelry Type</Label>
          <Select value={data.jewelryType} onValueChange={v => onUpdateField('jewelryType', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{JEWELRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="size" className="text-xs">Size</Label>
          <Input id="size" value={data.size} onChange={e => onUpdateField('size', e.target.value)} placeholder="e.g. 7" className="h-8 text-sm" />
        </div>
        <div>
          <Label htmlFor="date" className="text-xs">Date</Label>
          <Input id="date" type="date" value={data.date} onChange={e => onUpdateField('date', e.target.value)} className="h-8 text-sm" />
        </div>
        <div>
          <Label htmlFor="designer" className="text-xs">Designer Name</Label>
          <div className="flex items-center gap-1">
            <span className="text-sm text-muted-foreground font-medium">#</span>
            <Input id="designer" value={data.designerName} onChange={e => onUpdateField('designerName', e.target.value)} className="h-8 text-sm" placeholder="Designer" />
          </div>
        </div>
      </div>

      {/* Multi-Metal Section */}
      <div className="border border-border rounded-lg p-3 bg-card/50">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-semibold">Metal(s) & Weights</Label>
          {data.metals.length < 3 && (
            <Button variant="outline" size="sm" onClick={onAddMetal} className="h-6 text-xs px-2">
              <Plus className="h-3 w-3 mr-1" /> Add Metal
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {data.metals.map((metal) => {
            const metalType = metal.metal_type || inferMetalType(metal.karat, metal.color);
            const karatOpts = METAL_KARATS[metalType] || [];
            const colorOpts = METAL_COLOURS[metalType] || [];
            return (
              <div key={metal.id} className="flex items-center gap-2">
                <Select
                  value={metalType}
                  onValueChange={v => {
                    // Changing type invalidates karat/color — reset both to first valid option.
                    onUpdateMetal(metal.id, 'metal_type', v);
                    onUpdateMetal(metal.id, 'karat', (METAL_KARATS[v] || [])[0] || '');
                    onUpdateMetal(metal.id, 'color', (METAL_COLOURS[v] || [])[0] || '');
                  }}
                >
                  <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{METAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={karatOpts.includes(metal.karat) ? metal.karat : ''} onValueChange={v => onUpdateMetal(metal.id, 'karat', v)}>
                  <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue placeholder="Karat" /></SelectTrigger>
                  <SelectContent>{karatOpts.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={colorOpts.includes(metal.color) ? metal.color : ''} onValueChange={v => onUpdateMetal(metal.id, 'color', v)}>
                  <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue placeholder="Colour" /></SelectTrigger>
                  <SelectContent>{colorOpts.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-7 text-xs w-[100px]" value={metal.netWeight} onChange={e => onUpdateMetal(metal.id, 'netWeight', e.target.value)} placeholder="Net Wt (gms)" />
                {data.metals.length > 1 && (
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onRemoveMetal(metal.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3 pt-2 border-t border-border/50">
          <div>
            <Label className="text-xs text-muted-foreground">Net Weight (gms)</Label>
            <div className="text-sm font-medium text-foreground">{data.netWeight || '\u2014'}</div>
          </div>
          <div>
            <Label htmlFor="grossWeight" className="text-xs text-muted-foreground">Gross Weight (gms)</Label>
            <Input id="grossWeight" value={data.grossWeight} onChange={e => { onSetGrossWeightManual(true); onUpdateField('grossWeight', e.target.value); }} placeholder="Auto-calc" className="h-7 text-xs" />
            <div className="flex items-center gap-1.5 mt-1">
              <Checkbox id="grossAuto" checked={!data.grossWeightManual} onCheckedChange={(checked) => onSetGrossWeightManual(!checked)} className="h-3 w-3" />
              <label htmlFor="grossAuto" className="text-[10px] text-muted-foreground cursor-pointer">Auto-calculate</label>
            </div>
          </div>
        </div>
        <p className="text-[9px] text-muted-foreground italic leading-tight mt-1">* Net & gross weights do not include suggested findings weight.</p>
      </div>

      {/* Finishing — its own section to mirror the Task form layout */}
      <div className="border border-border rounded-lg p-3 bg-card/50">
        <Label className="text-xs font-semibold mb-2 block">Finishing</Label>
        <Select
          value={FINISHING_TYPES.includes(data.finishing) ? data.finishing : (data.finishing ? '__custom' : '')}
          onValueChange={v => {
            if (v === '__custom') onUpdateField('finishing', customFinishing || '');
            else onUpdateField('finishing', v);
          }}
        >
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select finishing..." /></SelectTrigger>
          <SelectContent>
            {FINISHING_TYPES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            <SelectItem value="__custom">Other...</SelectItem>
          </SelectContent>
        </Select>
        {data.finishing && !FINISHING_TYPES.includes(data.finishing) && (
          <Input className="h-7 text-xs mt-1" value={data.finishing} onChange={e => onUpdateField('finishing', e.target.value)} placeholder="Specify finishing..." />
        )}
      </div>

      {/* Findings */}
      {findingsOptions.length > 0 && (
        <div className="border border-border rounded-lg p-3 bg-card/50">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold">Findings ({data.jewelryType})</Label>
            <Button variant="outline" size="sm" onClick={onAddFinding} className="h-6 text-xs px-2" data-testid="cad-add-finding">
              <Plus className="h-3 w-3 mr-1" /> Add Finding
            </Button>
          </div>
          {data.findings.length === 0 && <p className="text-xs text-muted-foreground italic">No findings added yet.</p>}
          {data.findings.map(finding => (
            <div key={finding.id} className="flex items-center gap-2 mb-1.5">
              <Select value={finding.type} onValueChange={v => onUpdateFinding(finding.id, 'type', v)}>
                <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>{findingsOptions.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="h-7 text-xs w-[80px]" value={finding.customName || ''} onChange={e => onUpdateFinding(finding.id, 'customName', e.target.value)} placeholder="Name (e.g. ANC30)" />
              <Input className="h-7 text-xs w-[80px]" value={finding.weight} onChange={e => onUpdateFinding(finding.id, 'weight', e.target.value)} placeholder="Weight" />
              <Input className="h-7 text-xs w-[80px]" value={finding.details} onChange={e => onUpdateFinding(finding.id, 'details', e.target.value)} placeholder="Details" />
              <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onRemoveFinding(finding.id)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Stone Details */}
      <div className="border border-border rounded-lg p-3 bg-card/50">
        <div className="flex items-center justify-between mb-2">
          <div>
            <Label className="text-xs font-semibold">Stone Details</Label>
            {data.stones.length > 0 && (
              <span className="text-[10px] text-muted-foreground ml-2">Total: {totalStoneQty} pcs, {totalStoneWeight.toFixed(2)} ct</span>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} className="h-7 text-xs" title="Bulk add stones">
              <Grid className="h-3 w-3 mr-1" /> Bulk Add
            </Button>
            <Button variant="outline" size="sm" onClick={onAddStone} className="h-7 text-xs" data-testid="cad-add-stone">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
        </div>
        {data.stones.length === 0 && <p className="text-xs text-muted-foreground italic">No stones added yet.</p>}
        {data.stones.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {['Stone', 'Ownership', 'Cut', 'Category', 'Quality', 'Size', 'Ct. Wt', 'Qty', 'Total', ''].map(h => (
                    <th key={h} className="text-left py-1 px-1 font-medium text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.stones.map(stone => (
                  <tr key={stone.id} className="border-b border-border/30">
                    <td className="py-0.5 px-0.5">
                      {STONE_TYPES.includes(stone.stone_category) || !stone.stone_category ? (
                        <Select value={stone.stone_category || 'Natural'} onValueChange={v => { if (v === '__other') onUpdateStone(stone.id, 'stone_category', ''); else onUpdateStone(stone.id, 'stone_category', v); }}>
                          <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[78px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STONE_TYPES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            <SelectItem value="__other">Other...</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input className="h-6 text-[11px] p-1 w-[78px]" value={stone.stone_category || ''} onChange={e => onUpdateStone(stone.id, 'stone_category', e.target.value)} placeholder="Type..." />
                      )}
                    </td>
                    <td className="py-0.5 px-0.5">
                      {STONE_OWNERSHIPS.includes(stone.ownership) || !stone.ownership ? (
                        <Select value={stone.ownership || 'KS Goods'} onValueChange={v => { if (v === '__other') onUpdateStone(stone.id, 'ownership', ''); else onUpdateStone(stone.id, 'ownership', v); }}>
                          <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[78px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STONE_OWNERSHIPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            <SelectItem value="__other">Other...</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input className="h-6 text-[11px] p-1 w-[78px]" value={stone.ownership || ''} onChange={e => onUpdateStone(stone.id, 'ownership', e.target.value)} placeholder="Type..." />
                      )}
                    </td>
                    <td className="py-0.5 px-0.5">
                      {STONE_SHAPES.includes(stone.stone_shape || stone.type) ? (
                        <Select value={stone.stone_shape || stone.type || 'Round'} onValueChange={v => { if (v === '__other') onUpdateStone(stone.id, 'stone_shape', ''); else onUpdateStone(stone.id, 'stone_shape', v); }}>
                          <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[68px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{STONE_SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}<SelectItem value="__other">Other...</SelectItem></SelectContent>
                        </Select>
                      ) : (
                        <Input className="h-6 text-[11px] p-1 w-[68px]" value={stone.stone_shape || ''} onChange={e => onUpdateStone(stone.id, 'stone_shape', e.target.value)} placeholder="Cut..." />
                      )}
                    </td>
                    <td className="py-0.5 px-0.5">
                      <Select value={stone.category || 'Accent Stone'} onValueChange={v => onUpdateStone(stone.id, 'category', v)}>
                        <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[70px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STONE_POSITION_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={stone.stone_quality || stone.quality || ''} onChange={e => onUpdateStone(stone.id, 'stone_quality', e.target.value)} placeholder="VS1" /></td>
                    <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={stone.size_mm || stone.size || ''} onChange={e => onUpdateStone(stone.id, 'size_mm', e.target.value)} placeholder="mm" /></td>
                    <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" type="number" step="0.001" value={stone.weight_per_stone || stone.weight || ''} onChange={e => onUpdateStone(stone.id, 'weight_per_stone', e.target.value)} placeholder="ct" /></td>
                    <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-12" type="number" value={stone.quantity || ''} onChange={e => onUpdateStone(stone.id, 'quantity', e.target.value)} placeholder="Qty" /></td>
                    <td className="py-0.5 px-0.5 text-[11px] text-muted-foreground text-center">{((parseFloat(stone.weight_per_stone ?? stone.weight) || 0) * (parseInt(stone.quantity) || 0)).toFixed(2)}</td>
                    <td>
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => onRemoveStone(stone.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Engraving & Stamping — one combined section to mirror the Task form layout */}
      <div className="border border-border rounded-lg p-3 bg-card/50 space-y-3">
        <Label className="text-xs font-semibold block">Engraving &amp; Stamping</Label>

        <div>
          <Label htmlFor="engraving" className="text-xs">Engraving Instructions</Label>
          <Textarea id="engraving" value={data.engravingInstructions} onChange={e => onUpdateField('engravingInstructions', e.target.value)} placeholder="Engraving text or instructions..." rows={2} className="text-sm" />
        </div>
        <div>
          <Label className="text-xs">Engraving Image Reference</Label>
          {data.engravingImage?.url ? (
            <div className="relative w-32 h-32 border border-border rounded-lg overflow-hidden mt-1">
              <img src={data.engravingImage.url} alt="Engraving ref" className="w-full h-full object-contain bg-white" />
              <Button variant="ghost" size="icon" className="absolute top-0 right-0 h-6 w-6 bg-background/80 hover:bg-destructive hover:text-destructive-foreground" onClick={onRemoveEngravingImage}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <label className="flex items-center gap-2 mt-1 px-3 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors w-fit">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Upload engraving reference</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleEngravingImageSelect} />
            </label>
          )}
        </div>

        <div>
          <Label htmlFor="stampingInstructions" className="text-xs">Stamping Instructions</Label>
          <Textarea id="stampingInstructions" value={data.stampingInstructions || ''} onChange={e => onUpdateField('stampingInstructions', e.target.value)} placeholder="Hallmark, brand stamp, etc." rows={2} className="text-sm" />
        </div>
        <div>
          <Label className="text-xs">Stamping Image Reference(s)</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {(data.stampingImages || []).map((img, i) => (
              <div key={img.id} className="relative w-24 h-24 border border-border rounded-lg overflow-hidden">
                <img src={img.url} alt={`Stamping ref ${i + 1}`} className="w-full h-full object-contain bg-white" />
                <Button variant="ghost" size="icon" className="absolute top-0 right-0 h-6 w-6 bg-background/80 hover:bg-destructive hover:text-destructive-foreground" onClick={() => onRemoveStampingImage && onRemoveStampingImage(i)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <label className="flex flex-col items-center justify-center w-24 h-24 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground mt-1">Add stamping</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                const files = e.target.files;
                if (files && onAddStampingImage) Array.from(files).forEach(f => onAddStampingImage(f));
                e.target.value = '';
              }} />
            </label>
          </div>
        </div>
      </div>

      {/* Customer Reference Images */}
      <div>
        <Label className="text-xs">Customer Reference Images</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {data.customerImages.map((img, i) => (
            <div key={img.id} className="relative w-32 h-32 border border-border rounded-lg overflow-hidden">
              <img src={img.url} alt={`Customer ref ${i + 1}`} className="w-full h-full object-contain bg-white" />
              <Button variant="ghost" size="icon" className="absolute top-0 right-0 h-6 w-6 bg-background/80 hover:bg-destructive hover:text-destructive-foreground" onClick={() => onRemoveCustomerImage(i)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <label className="flex flex-col items-center justify-center w-32 h-32 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground mt-1">Add images</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleCustomerImageSelect} />
          </label>
        </div>
      </div>

      {/* Other Details */}
      <div>
        <Label htmlFor="otherDetails" className="text-xs">Other Details</Label>
        <Textarea id="otherDetails" value={data.otherDetails} onChange={e => onUpdateField('otherDetails', e.target.value)} placeholder="Additional notes..." rows={2} className="text-sm" />
      </div>

      {/* KS Notes */}
      <div>
        <Label htmlFor="ksNotes" className="text-xs">KS Notes</Label>
        <Textarea id="ksNotes" value={data.ksNotes || ''} onChange={e => onUpdateField('ksNotes', e.target.value)} placeholder="KS Notes..." rows={2} className="text-sm" />
      </div>

      <Button variant="outline" size="sm" onClick={onReset} className="w-full text-xs text-muted-foreground" data-testid="cad-reset-btn">
        <RotateCcw className="h-3 w-3 mr-1" /> Reset All Fields
      </Button>

      <StoneImportDialog open={importOpen} onOpenChange={setImportOpen} onImport={onImportStones} existingStones={data.stones} />
    </div>
  );
}
